export type EdrPlayer = {
  estimated_dynamic_rating?: number | string | null;
};

export type EdrLineupPlayer = EdrPlayer & {
  lineType?: string | null;
};

function numericEdr(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function average(values: Array<number | null>): number | null {
  const validValues = values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
  if (validValues.length === 0) return null;
  return validValues.reduce((sum, value) => sum + value, 0) / validValues.length;
}

export function formatEdr(value: unknown): string {
  const numericValue = numericEdr(value);
  return numericValue === null ? '—' : numericValue.toFixed(2);
}

export function hasEdr(value: unknown): boolean {
  return numericEdr(value) !== null;
}

export function calculateDoublesLineAvgEdr(playerA: EdrPlayer | undefined, playerB: EdrPlayer | undefined): number | null {
  return average([
    numericEdr(playerA?.estimated_dynamic_rating),
    numericEdr(playerB?.estimated_dynamic_rating),
  ]);
}

export function calculateSinglesAvgEdr(lineup: EdrLineupPlayer[]): number | null {
  return average(lineup.filter((player) => player.lineType === 'singles').map((player) => numericEdr(player.estimated_dynamic_rating)));
}

export function calculateDoublesAvgEdr(lineup: EdrLineupPlayer[]): number | null {
  return average(lineup.filter((player) => player.lineType !== 'singles').map((player) => numericEdr(player.estimated_dynamic_rating)));
}

export function calculateOverallAvgEdr(lineup: EdrLineupPlayer[]): number | null {
  return average(lineup.map((player) => numericEdr(player.estimated_dynamic_rating)));
}

export function calculateLineupAverageEdr(lineup: EdrLineupPlayer[]): number | null {
  return calculateOverallAvgEdr(lineup);
}

export function calculateEdrDifference(ourAvg: unknown, opponentAvg: unknown): number | null {
  const ourNumeric = numericEdr(ourAvg);
  const opponentNumeric = numericEdr(opponentAvg);
  if (ourNumeric === null || opponentNumeric === null) return null;
  return ourNumeric - opponentNumeric;
}

export function formatEdrDifference(value: unknown): string {
  const numericValue = numericEdr(value);
  if (numericValue === null) return '—';
  if (numericValue > 0) return `+${numericValue.toFixed(2)}`;
  return numericValue.toFixed(2);
}
